import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Cookie } from 'lucide-react';

const CookiePolicy = () => {
  const { language } = useLanguage();
  
  const getPath = (path) => {
    return language === 'en' ? `/en${path}` : path;
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-black">
      <div className="container mx-auto max-w-4xl">
        {/* Back button */}
        <Link 
          to={getPath('/')}
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-[#D6CE15] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          <span>{language === 'et' ? 'Tagasi avalehele' : 'Back to homepage'}</span>
        </Link>

        {/* Content */}
        <div className="prose prose-invert prose-green max-w-none">
          <div className="flex items-center gap-3 mb-6">
            <Cookie className="text-[#D6CE15]" size={40} />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-0">
              Küpsiste poliitika
            </h1>
          </div>
          
          <p className="text-gray-400 mb-8">Viimati uuendatud: 9. detsember 2025</p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Mis on küpsised?</h2>
              <p className="leading-relaxed">Küpsised on väikesed tekstifailid, mida veebisaidid salvestavad teie seadmesse kasutajakogemuse parandamiseks ja veebi liikluse analüüsimiseks. Küpsised ei kahjusta teie seadet ega sisalda viirusi.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Milliseid küpsiseid me kasutame?</h2>
              
              <div className="space-y-6">
                <div className="bg-[#0a0a0a] border border-[#D6CE15]/20 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-[#D6CE15] mb-3">Vajalikud küpsised</h3>
                  <p className="mb-4">Need küpsised on vajalikud veebisaidi tööks ega saa neid keelata.</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Seansi haldamine</li>
                    <li>Turvalisus ja autentimine</li>
                    <li>Vormide andmete salvestamine</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4"><strong>Säilitamisperiood:</strong> Seansi lõpuni</p>
                </div>

                <div className="bg-[#0a0a0a] border border-[#D6CE15]/20 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-[#D6CE15] mb-3">Analüütilised küpsised</h3>
                  <p className="mb-4">Aitavad meil mõista, kuidas külastajad saiti kasutavad.</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Google Analytics 4:</strong> Liikluse ja kasutajate käitumise analüüs</li>
                    <li>Kasutajakogemuse analüüs ja külastajate statistika</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4"><strong>Säilitamisperiood:</strong> Kuni 2 aastat</p>
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D6CE15] hover:text-[#A4A71E] text-sm mt-2 inline-block">
                    Privacy Policy →
                  </a>
                </div>

                <div className="bg-[#0a0a0a] border border-[#D6CE15]/20 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-[#D6CE15] mb-3">Funktsionaalsed küpsised</h3>
                  <p className="mb-4">Pakuvad täiustatud funktsioone ja isikupärastamist.</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Keele ja eelistuste salvestamine</li>
                    <li>Kasutajaliidese kohandamine</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4"><strong>Säilitamisperiood:</strong> Kuni 1 aasta</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Kuidas küpsiseid hallata?</h2>
              <p className="mb-4">Küpsiseid saate hallata meie sätete või oma brauseri sätete kaudu. Pange tähele, et küpsiste keelamine võib mõjutada veebisaidi funktsionaalsust.</p>
              <button
                onClick={() => {
                  const event = new CustomEvent('openCookieSettings');
                  window.dispatchEvent(event);
                }}
                className="inline-block bg-[#D6CE15] hover:bg-[#A4A71E] text-black font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                Ava küpsiste sätted
              </button>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Õiguslik alus</h2>
              <p className="mb-3">Küpsiste kasutamine põhineb:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Üldine andmekaitse määrus (GDPR)</li>
                <li>Eesti elektroonilise side seadus § 104</li>
                <li>Teie nõusolek (välja arvatud vajalikud küpsised)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Kontakt</h2>
              <p className="mb-4">Küpsistega seotud küsimuste korral võtke meiega ühendust:</p>
              <div className="bg-[#1a1a1a] border border-[#D6CE15]/10 rounded-lg p-6">
                <p className="text-white font-semibold mb-2">13Labs OÜ</p>
                <p className="text-gray-400 mb-2">Registrikood: 17350520</p>
                <p className="text-gray-400 mb-2">Aadress: Uuslinna tn 11, Lasnamäe linnaosa, 11415 Tallinn, Harju maakond</p>
                <p className="text-gray-400">Email: <a href="mailto:info@polyusplus.com" className="text-[#D6CE15] hover:text-[#A4A71E]">info@polyusplus.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;